import {
  Avatar,
  Badge,
  Checkbox,
  CrossIcon,
  EditIcon,
  IconButton,
  Menu,
  MoreIcon,
  Pane,
  Popover,
  Position,
  SideSheet,
  Spinner,
  Tab,
  Table,
  Tablist,
  Text,
  TextInputField,
  TickIcon,
  toaster,
  Tooltip,
} from "evergreen-ui";
import React, { useState } from "react";
import styled from "styled-components";
import { RouteComponentProps, withRouter } from "react-router-dom";
import Status from "../Status";
import { getDateWithTime } from "src/utilities";
import { gql, useMutation } from "@apollo/client";
import { LIST_SHIPMENT_ORDERS } from "src/AdminRoutes/ShipmentOrders/ShipmentOrders";
import useInput from "src/Hooks/useInput";
import Button from "../Button";

interface IProps extends RouteComponentProps<any> {
  record: any;
  showColumn: any;
  variables: any;
  index: number;
}
const Title = styled.h1`
  font-weight: 700;
  font-size: 1.2rem;
  margin-bottom: 1rem;
`;

export const UPDATE_RETAIL_ORDER = gql`
  mutation updateRetailOrder(
    $where: retail_order_pk_columns_input!
    $set: retail_order_set_input
  ) {
    update_retail_order_by_pk(pk_columns: $where, _set: $set) {
      id
      status
      shipped
      courier
      tracking_id
      tracking_link
      shipped_at
      __typename
    }
  }
`;

export const UPDATE_RB_ORDER = gql`
  mutation updateRBOrder(
    $where: rb_orders_pk_columns_input!
    $set: rb_orders_set_input
  ) {
    update_rb_orders_by_pk(pk_columns: $where, _set: $set) {
      id
      status
      shipped
      courier
      tracking_id
      tracking_link
      shipped_at
      __typename
    }
  }
`;

export const UPDATE_DRB_ORDER = gql`
  mutation updateDRBOrder(
    $where: drb_orders_pk_columns_input!
    $set: drb_orders_set_input
  ) {
    update_drb_orders_by_pk(pk_columns: $where, _set: $set) {
      id
      status
      shipped
      courier
      tracking_id
      tracking_link
      shipped_at
      __typename
    }
  }
`;

export const UPDATE_PRODUCT_ORDER = gql`
  mutation updateProductOrder(
    $where: product_order_pk_columns_input!
    $set: product_order_set_input
  ) {
    update_product_order_by_pk(pk_columns: $where, _set: $set) {
      id
      status
      shipped
      courier
      tracking_id
      tracking_link
      shipped_at
      __typename
    }
  }
`;
const AdminShipmentRow: React.FC<IProps> = ({
  record,
  showColumn,
  history,
  index,
  variables,
}) => {
  const tabs = ["Agent", "Payment", "Shipment Details", "Tracking Info"];
  const [showSide, setShowSide] = useState(false);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);

  const courier = useInput(record.courier || "");
  const tracking_id = useInput(record.tracking_id || "");
  const tracking_link = useInput(record.tracking_link || "");

  const checkMutationType = () => {
    switch (record.type) {
      case "mms":
        return UPDATE_RETAIL_ORDER;
      case "retail":
        return UPDATE_RETAIL_ORDER;
      case "normal":
        return UPDATE_PRODUCT_ORDER;
      case "drb":
        return UPDATE_DRB_ORDER;
      default:
        return UPDATE_RB_ORDER;
    }
  };

  const [updateOrder, { loading }] = useMutation(checkMutationType(), {
    update: (client, { data }) => {
      const queryResult = client.readQuery<any>({
        query: LIST_SHIPMENT_ORDERS,
        variables,
      });
      let returnData;
      if (checkMutationType() === UPDATE_RETAIL_ORDER) {
        returnData = data.update_retail_order_by_pk;
      }
      if (checkMutationType() === UPDATE_PRODUCT_ORDER) {
        returnData = data.update_product_order_by_pk;
      }
      if (checkMutationType() === UPDATE_RB_ORDER) {
        returnData = data.update_rb_orders_by_pk;
      }
      if (checkMutationType() === UPDATE_DRB_ORDER) {
        returnData = data.update_drb_orders_by_pk;
      }
      let orderIndex = queryResult?.orders_with_delivery.findIndex(
        (a) => a.order_id === returnData.id && a.type === record.type
      );
      let paginatedIndex = queryResult?.paginated.findIndex(
        (a) => a.order_id === returnData.id && a.type === record.type
      );
      let newPaginated = [...queryResult.paginated];
      newPaginated[paginatedIndex] = {
        ...newPaginated[paginatedIndex],
        status: returnData.status,
        shipped: returnData.shipped,
        shipped_at: returnData.shipped_at,
        courier: returnData.courier,
        tracking_id: returnData.tracking_id,
        tracking_link: returnData.tracking_link,
      };
      let newList = [...queryResult.orders_with_delivery];
      newList[orderIndex] = {
        ...newList[orderIndex],
        status: returnData.status,
        shipped: returnData.shipped,
        shipped_at: returnData.shipped_at,
        courier: returnData.courier,
        tracking_id: returnData.tracking_id,
        tracking_link: returnData.tracking_link,
      };
      client.writeQuery({
        query: LIST_SHIPMENT_ORDERS,
        variables,
        data: {
          ...queryResult,
          orders_with_delivery: newList,
          paginated: newPaginated,
        },
      });
    },
  });

  const checkType = () => {
    switch (record.type.toLowerCase()) {
      case "mms":
        return "MMS";
      case "retail":
        return "Retail";
      case "rb":
        return "RB";
      case "drb":
        return "DRB";
      case "normal":
        return "Request Shipment";
      default:
        return null;
    }
  };

  const options = {
    id: record.order_id,
    total_price: record.unit_price * record.quantity + record.delivery_rate,
    create_date: getDateWithTime(record.created_at),
    type: <Badge color="yellow">{checkType()}</Badge>,
    remark: record.remark,
    payment: record.payment_slip ? (
      <Tooltip
        appearance="card"
        content={
          <img
            style={{
              maxWidth: "100vw",
              maxHeight: "100vh",
              objectFit: "contain",
            }}
            onClick={() => {
              window.open(record.payment_slip);
            }}
            height="auto"
            width="auto"
            alt="payment slip"
            src={record.payment_slip}
          />
        }
      >
        <Badge color="green">VIEW</Badge>
      </Tooltip>
    ) : (
      <Badge color="red">UNPAID</Badge>
    ),
    full_address:
      record.address +
      ", " +
      record.city +
      ", " +
      record.state +
      ", " +
      record.postcode +
      ", " +
      record.name,
    postcode: record.postcode,
    recipient: record.recipient,
    status: <Status status={record.status} />,
    shipped: record.shipped ? (
      <Tooltip
        content={
          <Pane
            style={{
              padding: "1rem",
              backgroundColor: "white",
              borderRadius: ".3rem",
            }}
          >
            <Title>Tracking Information</Title>
            <Text>Courier: {record.courier || "N/A"}</Text> <br />
            <Text>Tracking No: {record.tracking_id || "N/A"}</Text> <br />
            <Text>Tracking Link: {record.tracking_link || "N/A"}</Text> <br />
            <Text>
              Last updated:{" "}
              {(record.shipped_at && getDateWithTime(record.shipped_at)) ||
                "N/A"}
            </Text>
          </Pane>
        }
      >
        <Badge color="blue">VIEW</Badge>
      </Tooltip>
    ) : (
      <Badge color="orange">NOT SHIPPED</Badge>
    ),
  };

  const product = (size = 40) =>
    record.products ? (
      <div>
        <div className=" flex flex-row items-center overflow-ellipsis w-full">
          <span className=" mr-2">
            <img
              alt={record.products.name}
              className={`box-${size} object-contain`}
              src={record.products.product_img}
            />
          </span>
          <p
            className={`text-xs ${
              size === 40 &&
              "w-100px whitespace-nowrap overflow-ellipsis overflow-hidden display:block"
            }`}
          >
            {record.products.name} <br />x {record.quantity}
          </p>
        </div>
        <br />
      </div>
    ) : record.multiproduct ? (
      <div>
        {JSON.parse(record.multiproduct).map((rec, index) => {
          return (
            <div key={index}>
              <div className=" flex flex-row items-center overflow-ellipsis w-full">
                <span className=" mr-2">
                  <img
                    width="60px"
                    height="60px"
                    alt={rec.name}
                    className={`box-${size} object-contain`}
                    src={rec.img}
                  />
                </span>
                <p
                  className={`text-xs ${
                    size === 40 &&
                    "w-100px whitespace-nowrap overflow-ellipsis overflow-hidden display:block"
                  }`}
                >
                  {rec.name}
                  <br />x {rec.quantity}
                </p>
              </div>
              <br />
            </div>
          );
        })}
      </div>
    ) : (
      "No product selected."
    );

  const toggleSide = () => {
    setShowSide(!showSide);
  };

  console.log(record);

  const onShipped = (e) => {
    let shipped = e.target.checked;
    if (!loading) {
      updateOrder({
        variables: {
          where: {
            id: record.id,
          },
          set: {
            shipped,
            shipped_at: new Date().toISOString(),
          },
        },
      })
        .then(() => {
          toaster.success("Changes saved!");
        })
        .catch((e) => {
          toaster.danger(e.message);
        });
    }
  };

  const onUpdateTracking = () => {
    if (!loading) {
      updateOrder({
        variables: {
          where: {
            id: record.id,
          },
          set: {
            courier: courier.value,
            tracking_id: tracking_id.value,
            tracking_link: tracking_link.value,
            shipped_at: new Date().toISOString(),
          },
        },
      })
        .then(() => {
          toaster.success("Changes saved!");
        })
        .catch((e) => {
          toaster.danger(e.message);
        });
    }
  };

  const onUpdateStatus = (status) => {
    if (!loading) {
      updateOrder({
        variables: {
          where: {
            id: record.id,
          },
          set: {
            status,
          },
        },
      })
        .then(() => {
          toaster.success("Changes saved!");
        })
        .catch((e) => {
          toaster.danger(e.message);
        });
    }
  };
  return (
    <Table.Row
      height="auto"
      paddingY={12}
      className={`${index % 2 === 0 && "bg-white"}`}
    >
      <Table.Cell flexBasis={200} flexShrink={0} flexGrow={0}>
        {record.user ? (
          <div className=" w-full flex flex-row items-center">
            <Avatar
              src={record.user.avatar}
              name={record.user.name}
              size={24}
              marginRight={".2em"}
            />
            <p className=" text-xs">
              {record.user.name} ({record.user.username})
            </p>
          </div>
        ) : (
          <p className=" text-xs">N/A</p>
        )}
      </Table.Cell>
      <Table.TextCell className=" px-6">{record.quantity}</Table.TextCell>
      <Table.Cell className=" px-2">{product()}</Table.Cell>
      <Table.TextCell>{options[showColumn]}</Table.TextCell>
      <Table.TextCell>{options.status}</Table.TextCell>
      <Table.TextCell>{options.shipped}</Table.TextCell>
      <Table.TextCell>
        <Popover
          position={Position.BOTTOM_RIGHT}
          content={({ close }) => (
            <Menu>
              <Menu.Group title="Basic Operations">
                <Menu.Item
                  disabled={
                    loading ||
                    record.status === "Approved" ||
                    record.status === "Cancelled"
                  }
                  onSelect={() => {
                    onUpdateStatus("Approved");
                    close();
                  }}
                  icon={TickIcon}
                  intent="success"
                >
                  {loading ? "Loading..." : "Approve"}
                </Menu.Item>
                <Menu.Item
                  disabled={
                    record.status === "Rejected" ||
                    record.status === "Cancelled"
                  }
                  onSelect={() => {
                    onUpdateStatus("Rejected");
                    close();
                  }}
                  icon={CrossIcon}
                  intent="warning"
                >
                  Reject
                </Menu.Item>
              </Menu.Group>
              <Menu.Divider />
              <Menu.Group title="Advanced Operations">
                <Menu.Item onSelect={toggleSide} icon={EditIcon}>
                  Modify
                </Menu.Item>
              </Menu.Group>
            </Menu>
          )}
        >
          <IconButton appearance="minimal" height={24} icon={MoreIcon} />
        </Popover>
      </Table.TextCell>
      <SideSheet
        preventBodyScrolling
        containerProps={{
          display: "flex",
          flex: "1",
          flexDirection: "column",
          maxWidth: "100vw",
        }}
        isShown={showSide}
      >
        <Pane
          position="sticky"
          top="0"
          zIndex={1}
          flexShrink={0}
          elevation={0}
          background="white"
        >
          <div className=" flex md:hidden flex-row w-full justify-end items-center p-2">
            <IconButton icon={CrossIcon} onClick={toggleSide} />
          </div>
          <div className=" flex flex-row items-center justify-between p-4">
            <p className=" text-xs text-gray-400">
              ID: {record.order_id} (
              {record.type === "normal" ? "Request Shipping" : record.type})
            </p>
            <p className=" text-xs text-gray-400">
              {getDateWithTime(record.created_at)}
            </p>
          </div>
          <div className=" flex flex-row px-2">
            <div className=" mr-2">{options.status}</div>
            <div className=" mr-2">{options.payment}</div>
            <div className=" mr-2">{options.shipped}</div>
          </div>
          <div>{product(100)} </div>
          {Boolean(record.remark) && (
            <div className=" p-3 pt-0 -mt-1.5">
              <div className=" rounded-sm bg-gray-50 border border-gray-100 p-3 w-full">
                <h1 className=" text-md font-semibold text-gray-800">Remark</h1>
                <p>{record.remark}</p>
              </div>
            </div>
          )}
          <Tablist>
            {tabs.map((tb, index) => (
              <Tab
                isSelected={selectedTab === tb}
                onSelect={() => {
                  setSelectedTab(tb);
                }}
              >
                {tb}
              </Tab>
            ))}
          </Tablist>
        </Pane>
        <div className=" p-4 flex flex-auto pb-96 bg-gray-50">
          {selectedTab === tabs[0] && (
            <div className=" sidesheet-tab-sections">
              <h1 className=" text-lg text-gray-700 font-bold">
                Agent Details
              </h1>
              {record.user ? (
                <>
                  <p className=" text-sm mt-1">Name: {record.user.name}</p>
                  <p className=" text-sm mt-1">
                    Username: {record.user.username}
                  </p>
                  <p className=" text-sm mt-1">E-mail: {record.user.email}</p>
                  <p className=" text-sm mt-1">Phone: {record.user.phone}</p>
                  <span
                    onClick={() => {
                      history.push("/admin/agents/" + record.user.username);
                    }}
                    className=" underline text-blue-400 cursor-pointer hover:opacity-70 mt-4"
                  >
                    View Agent
                  </span>
                </>
              ) : (
                <p className=" w-full text-center mt-4">No agent.</p>
              )}
            </div>
          )}
          {selectedTab === tabs[1] && (
            <div className=" sidesheet-tab-sections">
              <h1 className=" text-lg text-gray-700 font-bold">Payment</h1>
              <p className=" text-sm mt-1 text-gray-700">
                Unit Price: {record.unit_price}
              </p>
              <p className=" text-sm mt-1 text-gray-700">
                Delivery Fee: {record.delivery_rate}
              </p>
              <p className=" text-sm mt-1 text-gray-700">
                Quantity: {record.quantity}
              </p>
              <p className=" text-base mt-2 text-gray-700 font-bold">
                Total Payable: {options.total_price}
              </p>
              <p className=" text-base mt-4 text-gray-700 font-bold">
                Payment Slip
              </p>
              {record.payment_slip ? (
                <img
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                  }}
                  width="100%"
                  height="auto"
                  alt={"Payment Slip"}
                  src={record.payment_slip}
                />
              ) : (
                <p className=" text-center p-4">No payment slip yet.</p>
              )}
            </div>
          )}
          {selectedTab === tabs[2] && (
            <div className=" sidesheet-tab-sections">
              <h1 className=" text-lg text-gray-700 font-bold">
                Shipment Details
              </h1>
              <h1 className=" mt-4 font-semibold text-gray-600">
                Contact information
              </h1>
              <p className=" mt-1 text-sm">
                Recipient Name: {record.recipient}
              </p>
              <p className=" mt-1 text-sm">Phone no: {record.phone}</p>
              <h1 className=" mt-4 font-semibold text-gray-600">
                Delivery address
              </h1>
              <p className=" mt-1 text-sm">Address: {record.address}</p>
              <p className=" mt-1 text-sm">City: {record.city}</p>
              <p className=" mt-1 text-sm">State: {record.state}</p>
              <p className=" mt-1 text-sm">Postcode: {record.postcode}</p>
              <p className=" mt-1 text-sm">Country: {record.name}</p>
            </div>
          )}
          {selectedTab === tabs[3] && (
            <div className=" sidesheet-tab-sections">
              <h1>Delivery</h1>
              {loading ? (
                <Spinner />
              ) : (
                <Checkbox
                  checked={record.shipped}
                  onChange={onShipped}
                  label={`Mark as ${
                    record.shipped ? "not shipped." : "shipped."
                  }`}
                  className=" z-1"
                />
              )}
              {record.shipped_at && (
                <p className=" text-gray-500 text-xs">
                  Last updated at: {getDateWithTime(record.shipped_at)}
                </p>
              )}
              <h1 className=" mt-4 font-semibold text-gray-600">
                Tracking Details
              </h1>
              <TextInputField
                {...courier}
                label="Courier"
                placeholder="Pos Laju"
              />
              <TextInputField
                {...tracking_id}
                label="Tracking no"
                placeholder="P11xx22xxxxxxx"
              />
              <TextInputField
                {...tracking_link}
                label="Tracking link"
                placeholder="https://poslaju.com/p11xx22xxxxxx"
              />
              <Button
                onClick={onUpdateTracking}
                loading={loading}
                width="100%"
                label="Save"
                disabled={
                  loading ||
                  (courier.value === record.courier &&
                    tracking_id.value === record.tracking_id &&
                    tracking_link.value === record.tracking_link)
                }
              />
            </div>
          )}
        </div>
      </SideSheet>
    </Table.Row>
  );
};

export default withRouter(AdminShipmentRow);
